import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Icon,
  Radio,
  RadioGroup,
  SimpleGrid,
  Spacer,
  Text,
  useColorModeValue,
  Wrap,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "../components/header.css";
import JobCard from "../components/JobCard";
import { getJobs } from "../redux/actions/jobActions";
import { MdSort } from "react-icons/md";

const JobsScreen = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, error, jobs } = useSelector((state) => state.job);
  const [listType, setListType] = useState("1");
  const isProcessingRef = useRef(false);
  const [sortBy, setSortBy] = useState("eta");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const headerBg = useColorModeValue("gray.100", "gray.800");
  const contentBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Initialize listType on mount and when pathname/state changes
  useEffect(() => {
    // Prevent double-processing from the state clearing
    if (isProcessingRef.current) {
      isProcessingRef.current = false;
      return;
    }

    const savedListType = localStorage.getItem("jobsListType");
    // Check if coming from header - state will have fromHeader: true
    const isFromHeader = location.state?.fromHeader === true;

    // Determine which listType to load
    const typeToLoad = isFromHeader ? "1" : savedListType || "1";

    setListType(typeToLoad);
    dispatch(getJobs(typeToLoad));

    // Clear the state immediately to prevent it persisting in history
    if (isFromHeader) {
      isProcessingRef.current = true;
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.key]);

  // Refresh interval - only depends on listType
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch(getJobs(listType));
      }
    }, 8000); //set your time here. repeat every 8 seconds

    return () => clearInterval(interval);
  }, [dispatch, listType]);

  const handleListTypeChange = (value) => {
    setListType(value);
    localStorage.setItem("jobsListType", value);
    dispatch(getJobs(value));
    // Reset selection mode when changing list type
    setSelectionMode(false);
    setSelectedJobs([]);
  };

  const handleSortByChange = (value) => {
    setSortBy(value)
    setSortOrder(value === "eta" ? "asc" : "desc")
  }

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedJobs([]);
  };

  const handleJobSelection = (jobId) => {
    setSelectedJobs((prev) => 
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs.map((job) => job.JOB_ID));
    }
  };

  const handleBulkSendSMS = () => {
    const selectedJobData = jobs.filter((job) => selectedJobs.includes(job.JOB_ID));
    navigate("/send-sms/bulk", { state: { jobs: selectedJobData } });
  };

  return (
    <>
      {
        // loading ? (
        //   <Box textAlign={"center"} mt={8}>
        //     <Text fontSize='xl'>Loading</Text>
        //     <Spinner size='xl' />
        //   </Box>
        // ) :
        error ? (
          <Alert status='error'>
            <AlertIcon />
            <AlertTitle>We are sorry!!!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <>
            <Flex
              direction='column'
              className='sticky-header2'
              justifyContent='center'
              align='center'
              width='100%'
              h='100px'
            >
              <Box bg={headerBg} p={3} w='100%'>
                <HStack justifyContent='center' align='center'>
                  <RadioGroup defaultValue='1' value={listType} onChange={handleListTypeChange} ml='2'>
                    <Radio ml='0' value='1'>
                      <Text fontWeight={listType === "1" ? "bold" : "normal"}>Jobs</Text>
                    </Radio>
                    <Radio ml='3' value='4'>
                      <Text fontWeight={listType === "4" ? "bold" : "normal"}>Bookings</Text>
                    </Radio>
                    <Radio ml='3' value='2'>
                      <Text fontWeight={listType === "2" ? "bold" : "normal"}>Completed</Text>
                    </Radio>
                    <Radio ml='3' value='3'>
                      <Text fontWeight={listType === "3" ? "bold" : "normal"}>Picked Up</Text>
                    </Radio>
                  </RadioGroup>
                </HStack>
              </Box>
              <Flex
                justifyContent='center'
                align='center'
                width='100%'
                //width='350px'
                bg={contentBg}
                p={2}
                borderBottomWidth='1px'
                borderBottomColor={borderColor}
                flexWrap='wrap'
                rowGap={2}
                columnGap={3}
              >
                <Heading as='h2' textAlign='center' size={{base: 'sm', md: 'md'}} noOfLines={1} mr={2} fontFamily='Verdana'>
                  {listType === "1"
                    ? "Jobs"
                    : listType === "4"
                    ? "Bookings"
                    : listType === "2"
                    ? "Completed Jobs"
                    : listType === "3"
                    ? "Picked-Up Jobs"
                    : "Jobs"}
                </Heading>{" "}
                <Badge
                  p='2'
                  rounded='full'
                  fontSize='sm'
                  fontWeight='bold'
                  mt='1'
                  colorScheme='teal'
                  minW='38px'
                  aspectRatio={1}
                  textAlign='center'
                >
                  {jobs.length}
                </Badge>
                <Button ml='4' variant='link' onClick={() => dispatch(getJobs(listType))} color={loading ? "gray.200" : "blue.500"} size='sm'>
                  &#x21bb; Refresh
                </Button>
                <Button ml='4' colorScheme='blue' size='sm' onClick={() => navigate("/jobs/new")}>
                  + New Job
                </Button>
                {listType === "3" && (
                  <Wrap spacing='8px' ml={{ base: 0, md: 2 }} justify='center'>
                    <Button
                      colorScheme={selectionMode ? "red" : "purple"}
                      size='sm'
                      onClick={toggleSelectionMode}
                      minW='100px'
                    >
                      {selectionMode ? "Close" : "Select"}
                    </Button>
                    {selectionMode && (
                      <>
                        <Button colorScheme='gray' size='sm' onClick={handleSelectAll} minW='100px'>
                          {selectedJobs.length === jobs.length ? "Deselect" : "All"}
                        </Button>
                        <Button
                          colorScheme='green'
                          size='sm'
                          onClick={handleBulkSendSMS}
                          isDisabled={selectedJobs.length === 0}
                          minW='100px'
                        >
                          Send SMS ({selectedJobs.length})
                        </Button>
                      </>
                    )}
                  </Wrap>
                )}
                {/* {loading ? (
                  <Box textAlign='center' ml={4} minW='50px'>
                    <Spinner size='sm' />
                  </Box> */}
                {/* {loading ? (
                  <Text fontSize='xs' color='gray.500' textAlign='center' ml={4} mt='1'>
                    Refreshing...
                  </Text>
                ) : (
                  // <Box ml={4} bg='gray.100' minW='80px' minH='20px'></Box>
                  <Text fontSize='xs' color={hiddenTextColor} textAlign='center' ml={4} mt='1'>
                    Refreshing...
                  </Text>
                )} */}
                <Popover placement='bottom-start'>
                  <PopoverTrigger>
                    <Box cursor='pointer'>
                      <Icon
                        as={MdSort}
                        ml='4'
                        boxSize='5'
                        color='gray.500'
                        title={
                          sortBy === "eta"
                            ? `Sorted by ETA (${sortOrder === "asc" ? "Asc" : "Desc"}), then Job Number`
                            : `Sorted by Date Taken (${sortOrder === "asc" ? "Asc" : "Desc"}), then Job Number`
                        }
                      />
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent w='240px'>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader fontWeight='bold'>Sort Jobs</PopoverHeader>
                    <PopoverBody>
                      <Text fontSize='sm' fontWeight='semibold' mb='2'>Sort By</Text>
                      <RadioGroup value={sortBy} onChange={handleSortByChange}>
                        <Flex direction='column' gap={2}>
                          <Radio value='eta'>By ETA</Radio>
                          <Radio value='jobDate'>By Date Taken</Radio>
                        </Flex>
                      </RadioGroup>
                      <Text fontSize='sm' fontWeight='semibold' mt='4' mb='2'>Order</Text>
                      <RadioGroup value={sortOrder} onChange={(val) => setSortOrder(val)}>
                        <HStack spacing='4'>
                          <Radio value='asc'>Ascending</Radio>
                          <Radio value='desc'>Descending</Radio>
                        </HStack>
                      </RadioGroup>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </Flex>
              {jobs.length === 0 && (
                <Heading textAlign='center' size='md' fontWeight='normal'>
                  {listType === "4" ? "No Bookings found" : "No Jobs found"}
                </Heading>
              )}
            </Flex>

            {jobs.length >= 1 && (
              <Box pt={{ base: 4, md: 2 }}>
                <SimpleGrid
                  spacingX='12px'
                  spacingY='12px'
                  minChildWidth='420px'
                  justifyItems='stretch'
                  alignItems='center'
                  mx={{ base: "0", md: "8", lg: "28" }}
                  mt='4'
                >
                  {[...jobs]
                    .sort((a, b) => {
                      const ascCompare = () => {
                        if (sortBy === "eta") {
                          const etaA = a.ETA ? new Date(a.ETA).getTime() : 0
                          const etaB = b.ETA ? new Date(b.ETA).getTime() : 0
                          if (etaA !== etaB) return etaA - etaB
                        } else {
                          const dA = a.JOB_DATE ? new Date(a.JOB_DATE).getTime() : 0
                          const dB = b.JOB_DATE ? new Date(b.JOB_DATE).getTime() : 0
                          if (dA !== dB) return dA - dB
                        }
                        const jA = typeof a.JOB_NO === "number" ? a.JOB_NO : Number(a.JOB_NO || 0)
                        const jB = typeof b.JOB_NO === "number" ? b.JOB_NO : Number(b.JOB_NO || 0)
                        return jA - jB
                      }
                      const cmp = ascCompare()
                      return sortOrder === "asc" ? cmp : -cmp
                    })
                    .map((job) => (
                      <JobCard 
                        key={job.JOB_ID} 
                        job={job} 
                        loading={false} 
                        listType={listType}
                        selectionMode={selectionMode}
                        isSelected={selectedJobs.includes(job.JOB_ID)}
                        onSelectionChange={handleJobSelection}
                      />
                    ))}
                </SimpleGrid>
                <Spacer mt='100px' />
              </Box>
            )}
          </>
        )
      }
    </>
  );
};

export default JobsScreen;
